import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';

/**
 * Central reaction to an expired / rejected session.
 *
 * Apollo's `onError` link runs in a module-level singleton client, so — exactly
 * like the token + tenant getters in `auth.ts` — it cannot use Auth0 hooks
 * directly. A React component installs the redirect handler via
 * `useInstallAuthExpiryHandler`, and the error link calls `notifyAuthExpired()`
 * when a request comes back `401`.
 *
 * Why `loginWithRedirect` rather than `navigate('/signin')`: when Auth0 still
 * holds a (stale) session it reports `isAuthenticated === true`, and
 * `SignInPage` bounces an authenticated visitor straight back to the app — so a
 * router redirect to `/signin` would loop. `loginWithRedirect` always re-runs
 * the Auth0 exchange, refreshing the session and returning the user to where
 * they were.
 */

type AuthExpiredHandler = () => void;

let handler: AuthExpiredHandler | null = null;
// Guards against a redirect storm: several in-flight queries can each 401 in
// the same tick. The first notification wins; the rest are ignored until a
// fresh handler is installed (i.e. after the redirect round-trip re-mounts).
let redirecting = false;

/**
 * Install (or clear) the reaction to an expired session. Reinstalling resets
 * the once-guard — also the reset hook tests use between cases.
 */
export function setAuthExpiredHandler(fn: AuthExpiredHandler | null): void {
  handler = fn;
  redirecting = false;
}

/**
 * True when a network error means the request was rejected as unauthorized
 * (expired / invalid token). Apollo's `ServerError` carries `statusCode`. Pure
 * + exported for testing.
 */
export function isUnauthorizedNetworkError(
  networkError: (Error & { statusCode?: number }) | null | undefined,
): boolean {
  return networkError?.statusCode === 401;
}

/**
 * Notify the installed handler that the session has expired. No-op until a
 * handler is installed (boot / tests), and only fires once per session-death.
 */
export function notifyAuthExpired(): void {
  if (redirecting || handler === null) return;
  redirecting = true;
  handler();
}

/**
 * Install the Auth0 re-login as the global auth-expiry handler. Mount inside the
 * Auth0Provider tree (uses `useAuth0`) — see `AuthLinkInstaller` in
 * `Providers.tsx`. On expiry it re-runs `loginWithRedirect`, carrying the
 * current location as `returnTo` so the user lands back where they were.
 */
export function useInstallAuthExpiryHandler(): void {
  const { loginWithRedirect } = useAuth0();
  useEffect(() => {
    setAuthExpiredHandler(() => {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      void loginWithRedirect({ appState: { returnTo } });
    });
    return () => {
      setAuthExpiredHandler(null);
    };
  }, [loginWithRedirect]);
}
